/**
 * proseHtml.js, shared utility for consistent rich-text paragraph rendering.
 *
 * Handles four content formats that appear in the description field:
 *   1. Plain text with \n paragraph breaks (AI-generated, most common)
 *   2. Plain text with \n\n paragraph breaks
 *   3. HTML with multiple <p> tags (TipTap-saved content)
 *   4. HTML with <br>-separated lines in a single <p> (legacy TipTap collapse)
 *
 * Returns HTML with margin-top:1.1em injected inline on every top-level block
 * element after the first, beats the global * { margin:0 } CSS reset without
 * relying on class injection.
 */

const BLOCK = new Set(['P','H1','H2','H3','H4','H5','H6','UL','OL','BLOCKQUOTE','DIV','PRE']);

export function proseHtml(raw) {
  if (!raw || typeof raw !== 'string') return '';

  // ── Plain text path: every newline = paragraph break ─────────────────────
  if (!/<[a-z]/i.test(raw)) {
    const paras = raw.trim().split(/\n+/).map(p => p.trim()).filter(Boolean);
    if (paras.length <= 1) return `<p>${raw.trim()}</p>`;
    return paras
      .map((p, i) => `<p${i > 0 ? ' style="margin-top:1.1em"' : ''}>${p}</p>`)
      .join('');
  }

  // ── HTML path: DOMParser + inject margin between block elements ───────────
  const doc = new DOMParser().parseFromString(`<div>${raw}</div>`, 'text/html');
  const container = doc.body.firstChild;

  // Expand <br>-heavy paragraphs: a single <p> with <br> separators becomes
  // multiple <p> tags so each section gets its own paragraph margin.
  Array.from(container.querySelectorAll('p')).forEach(p => {
    const inner = p.innerHTML;
    if (!/<br\s*\/?>/i.test(inner)) return;
    const lines = inner.split(/<br\s*\/?>/i).map(l => l.trim()).filter(Boolean);
    if (lines.length <= 1) return;
    const frag = doc.createDocumentFragment();
    lines.forEach(line => {
      const newP = doc.createElement('p');
      newP.innerHTML = line;
      frag.appendChild(newP);
    });
    p.replaceWith(frag);
  });

  // Inject margin-top on every block element after the first
  let first = true;
  for (const child of Array.from(container.childNodes)) {
    if (child.nodeType === 1 && BLOCK.has(child.tagName)) {
      if (first) { first = false; }
      else { child.style.marginTop = '1.1em'; }
    } else if (child.nodeType === 3 && child.textContent.trim()) {
      const p = doc.createElement('p');
      p.textContent = child.textContent;
      if (!first) p.style.marginTop = '1.1em';
      first = false;
      child.replaceWith(p);
    }
  }

  return container.innerHTML;
}

/**
 * Convert value to TipTap-ready HTML before calling editor.setContent().
 * TipTap wraps plain text in ONE <p>, collapsing all paragraph breaks.
 * Pre-converting keeps each line as a separate ProseMirror block node.
 */
export function toEditorHtml(value) {
  if (!value || typeof value !== 'string') return '';
  if (/<[a-z]/i.test(value)) return value; // already HTML
  // Split on any newline, matches how proseHtml renders plain text
  const paras = value.trim().split(/\n+/).map(p => p.trim()).filter(Boolean);
  return paras.map(p => `<p>${p}</p>`).join('');
}

/**
 * Parse description into an array of paragraph objects for React rendering.
 * Each item: { html: boolean, content: string }
 *
 * Use this instead of dangerouslySetInnerHTML on the container so paragraph
 * margins can be applied via React style props (DOMPurify-proof).
 */
export function splitDescriptionBlocks(raw) {
  if (!raw || typeof raw !== 'string') return [];

  // Plain text: every newline is a paragraph break
  if (!/<[a-z]/i.test(raw)) {
    return raw.trim().split(/\n+/).map(p => p.trim()).filter(Boolean)
      .map(text => ({ html: false, content: text }));
  }

  // HTML: extract block-level elements
  const doc = new DOMParser().parseFromString(`<div>${raw}</div>`, 'text/html');
  const container = doc.body.firstChild;
  const blocks = [];

  container.childNodes.forEach(node => {
    if (node.nodeType === 1 && BLOCK.has(node.tagName)) {
      const inner = node.innerHTML;
      // Expand <br>-separated content into individual paragraphs
      if (/<br\s*\/?>/i.test(inner)) {
        inner.split(/<br\s*\/?>/i).map(l => l.trim()).filter(Boolean)
          .forEach(line => blocks.push({ html: true, content: line }));
      } else if (inner.trim()) {
        blocks.push({ html: true, content: inner });
      }
    } else if (node.nodeType === 3 && node.textContent.trim()) {
      blocks.push({ html: false, content: node.textContent.trim() });
    }
  });

  return blocks;
}
